import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';
import { PropertySelect } from '../../../components/form/property-select.js';
import { PrimaryCell, RowActions } from '../../../components/resource-list/cells.js';
import { ResourceListPage } from '../../../components/resource-list/resource-list-page.js';
import { type ColumnDescriptor } from '../../../lib/resource-list/columns.js';
import { roomTypeEndpoints } from '../api/endpoints.js';
import { useSoftDeleteRoomType } from '../api/mutations.js';
import { type RoomTypeRow, roomTypeRow } from '../api/schemas.js';

/**
 * Room types for one property.
 *
 * The property is chosen with a dropdown that is part of the URL, so a selected
 * property survives a refresh and can be linked. The same property is carried
 * into the create form.
 */

function formatDate(value: string | undefined): string {
  if (value === undefined) return '';
  return new Date(value).toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function Rooms({
  total,
  sellable,
}: {
  total: number | undefined;
  sellable: number | undefined;
}): React.JSX.Element {
  const t = total ?? 0;
  const s = sellable ?? 0;
  if (t === 0) return <span className="text-muted-foreground">—</span>;
  if (s === t) return <span>{t}</span>;
  return (
    <span className="whitespace-nowrap">
      <span className="font-medium">{s}</span>
      <span className="text-muted-foreground"> of {t} sellable</span>
    </span>
  );
}

export const roomTypeColumns: readonly ColumnDescriptor<RoomTypeRow>[] = [
  {
    id: 'name',
    header: 'Room type',
    sortable: true,
    alwaysVisible: true,
    fields: ['name', 'code'],
    cell: (row) => (
      <PrimaryCell
        label={row.name ?? row.id}
        to={`/room-types/${row.id}`}
        secondary={row.code ?? null}
      />
    ),
  },
  {
    id: 'maxOccupancy',
    header: 'Sleeps',
    sortable: true,
    fields: ['maxOccupancy'],
    cell: (row) => (row.maxOccupancy === undefined ? '—' : String(row.maxOccupancy)),
  },
  {
    id: 'rooms',
    header: 'Rooms',
    fields: ['totalRooms', 'sellableRooms'],
    cell: (row) => <Rooms total={row.totalRooms} sellable={row.sellableRooms} />,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    fields: ['status'],
    cell: (row) =>
      row.status === undefined ? null : (
        <StatusBadge tone={toneForStatus(row.status)}>{row.status}</StatusBadge>
      ),
  },
  {
    id: 'createdAt',
    header: 'Created',
    sortable: true,
    fields: ['createdAt'],
    defaultVisible: false,
    cell: (row) => formatDate(row.createdAt),
  },
  {
    id: 'actions',
    header: '',
    headerLabel: 'Actions',
    align: 'right',
    alwaysVisible: true,
    fields: ['name', 'version'],
    cell: (row: RoomTypeRow): React.JSX.Element => <RoomTypeRowActions row={row} />,
  },
];

function RoomTypeRowActions({ row }: { readonly row: RoomTypeRow }): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const softDelete = useSoftDeleteRoomType();
  const label = row.name ?? row.id;

  return (
    <>
      <div className="flex flex-nowrap items-center justify-end gap-1">
        <RowActions
          actions={[
            { label: `View ${label}`, icon: Eye, to: `/room-types/${row.id}` },
            { label: `Edit ${label}`, icon: Pencil, to: `/room-types/${row.id}/edit` },
          ]}
        />
        <PermissionGuard permission={PERMISSIONS.ROOM_TYPE_DELETE}>
          <RowActions
            actions={[
              {
                label: `Delete ${label}`,
                icon: Trash2,
                destructive: true,
                onClick: () => {
                  setConfirming(true);
                },
              },
            ]}
          />
        </PermissionGuard>
      </div>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Delete room type"
        description={`${label} will be removed from this list. Existing reservations and rooms that reference it are retained.`}
        confirmLabel="Delete"
        destructive
        loading={softDelete.isPending}
        onConfirm={() => {
          softDelete.mutate(
            { roomTypeId: row.id, version: row.version },
            {
              onSuccess: () => {
                setConfirming(false);
              },
            },
          );
        }}
      />
    </>
  );
}

export function RoomTypeListPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const propertyId = params.get('propertyId') ?? '';

  const updateProperty = (next: string): void => {
    const updated = new URLSearchParams(params);
    if (next === '') {
      updated.delete('propertyId');
    } else {
      updated.set('propertyId', next);
    }
    updated.delete('page');
    setParams(updated, { replace: true });
  };

  if (propertyId === '') {
    return (
      <PermissionGuard permission={PERMISSIONS.ROOM_TYPE_READ} fallback={<NotPermitted />}>
        <section className="space-y-section">
          <div>
            <h1 className="text-page-title font-semibold">Room types</h1>
            <p className="text-body text-muted-foreground">
              Room types are scoped to one property. Choose a property to see its room types.
            </p>
          </div>
          <div className="max-w-sm">
            <PropertySelect
              value=""
              onChange={updateProperty}
              description="Choose the property whose room types you want to manage."
              required
            />
          </div>
        </section>
      </PermissionGuard>
    );
  }

  const newLink = `/room-types/new?propertyId=${encodeURIComponent(propertyId)}`;

  return (
    <ResourceListPage
      title="Room types"
      description="Every room type for the selected property."
      path={roomTypeEndpoints.list}
      resource="roomTypes"
      schema={roomTypeRow}
      columns={roomTypeColumns}
      rowKey={(row) => row.id}
      permission={PERMISSIONS.ROOM_TYPE_READ}
      scope={{ propertyId }}
      defaultSort={{ columnId: 'name', direction: 'asc' }}
      emptyTitle="No room types yet"
      emptyDescription="A property cannot sell anything until it has at least one room type."
      actions={
        <PermissionGuard permission={PERMISSIONS.ROOM_TYPE_CREATE}>
          <Button asChild>
            <Link to={newLink}>
              <Plus className="h-4 w-4" />
              New room type
            </Link>
          </Button>
        </PermissionGuard>
      }
      filterPrefix={
        <div className="w-full max-w-sm">
          <PropertySelect value={propertyId} onChange={updateProperty} hideDescription />
        </div>
      }
    />
  );
}

function NotPermitted(): React.JSX.Element {
  return (
    <div className="text-body text-muted-foreground">
      You do not have permission to view room types.
    </div>
  );
}
